import { __ } from '@wordpress/i18n';
import { useCallback, useState } from 'preact/hooks';

import { ProgramTerm, ProgramChildTerm } from '../../../../types';
import { CaretIcon } from '../icons/icons';

function ChildCheckbox( { item, programs, handleProgramChange }: {
	item: ProgramChildTerm;
	programs: string[];
	handleProgramChange: Function;
} ) {
	const handleChildCheckbox = useCallback( ( event: Event, slug: string ) => {
		const input = event.target as HTMLInputElement;
		const isChecked = input.checked;

		const nextPrograms = isChecked ? [ ...programs, slug ] : programs.filter( ( child ) => child !== slug );
		handleProgramChange( nextPrograms );
	}, [ handleProgramChange, programs ] );

	const { slug, term_id, name } = item;

	if ( ! slug || ! term_id || ! name ) {
		return null;
	}

	return (
		<li className="tcc-map__checkbox-list-item">
			<div className="tcc-map__checkbox-wrapper">
				<input
					type="checkbox"
					id={ `tcc-map__checkbox--${ term_id }-${ slug }` }
					value={ slug }
					checked={ programs.includes( slug ) }
					onChange={ ( event ) => handleChildCheckbox( event, slug ) }
				/>
				<label htmlFor={ `tcc-map__checkbox--${ term_id }-${ slug }` }>{ name }</label>
			</div>
		</li>
	);
}

export default function ProgramCheckbox( { item, programs, handleProgramChange }: {
	item: ProgramTerm;
	programs: string[];
	handleProgramChange: Function;
} ) {
	const [ isOpen, setIsOpen ] = useState( false );

	const childrenSlugs = item.children.map( ( child ) => child.slug );

	const handleChange = useCallback( ( event: Event ) => {
		const input = event.target as HTMLInputElement;
		const isChecked = input.checked;

		// Find any child items that are not in programs (prevents adding duplicates when checking parent).
		const childrenNotInPrograms = childrenSlugs.filter( ( childSlug ) => ! programs.includes( childSlug ) );

		// Add / remove children based on checked.
		const nextPrograms = isChecked ? [ ...programs, ...childrenNotInPrograms ] : programs.filter( ( childSlug ) => ! childrenSlugs.includes( childSlug ) );
		handleProgramChange( nextPrograms );
	}, [ childrenSlugs, handleProgramChange, programs ] );

	// Return early if no child terms.
	if ( ! childrenSlugs.length ) {
		return null;
	}

	// Check parent if all children are checked.
	const checked = childrenSlugs.every( ( childSlug ) => programs.includes( childSlug ) );

	return (
		<>
			<li className="tcc-map__checkbox-list-item">
				<div className="tcc-map__checkbox-wrapper has-button">
					<div class="tcc-map__checkbox-input-wrapper">
						<input
							type="checkbox"
							id={ `tcc-map__checkbox--${ item.term_id }-${ item.slug }` }
							value={ item.slug }
							checked={ checked }
							onChange={ ( event ) => handleChange( event ) }
						/>
						<label htmlFor={ `tcc-map__checkbox--${ item.term_id }-${ item.slug }` }>All { item.name }</label>
					</div>
					<button
						className="tcc-map__checkbox-list-toggle"
						type="button"
						aria-expanded={ isOpen }
						aria-controls={ `tcc-map__checkbox-list--${ item.term_id }-${ item.slug }` }
						onClick={ () => setIsOpen( ! isOpen ) }
					>
						<span className="screen-reader-text">
							{ isOpen ? __( 'Collapse', 'tcc-map' ) : __( 'Expand', 'tcc-map' ) } { item.name }
						</span>
						<CaretIcon />
					</button>
				</div>

				<ul className={ `tcc-map__checkbox-list tcc-map__checkbox-list--child ${ isOpen ? 'visible' : '' }` } id={ `tcc-map__checkbox-list--${ item.term_id }-${ item.slug }` }>
					{ item.children.map( ( subitem ) => (
						<ChildCheckbox
							key={ subitem.term_id }
							item={ subitem }
							programs={ programs }
							handleProgramChange={ handleProgramChange }
						/>
					) ) }
				</ul>
			</li>
		</>
	);
}
